import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Database,
  Snowflake,
  ArrowLeft,
  Loader2,
  Plug,
  Check,
  AlertTriangle,
  Key,
  Lock,
} from "lucide-react";
import { api } from "../api";
import type { DatabaseConnectionParams, UploadResponse } from "../types";
import GreenTendrils from "./GreenTendrils";

const DB_TYPES = [
  { value: "snowflake", label: "Snowflake", icon: Snowflake },
  { value: "postgres", label: "PostgreSQL", icon: Database },
  { value: "mysql", label: "MySQL", icon: Database },
  { value: "sqlserver", label: "SQL Server", icon: Database },
  { value: "oracle", label: "Oracle", icon: Database },
];

export interface MdmConnectorCard {
  id: string;
  name: string;
  table_or_query?: string;
  record_count: number;
  last_sync_date: string;
}

interface Props {
  onComplete: (data: UploadResponse) => void;
  onBack: () => void;
  mode?: "profiler" | "mdm";
  onMdmConnectorCreated?: (card: MdmConnectorCard) => void;
  initialValues?: Partial<DatabaseConnectionParams>;
}

const EMPTY_FORM: DatabaseConnectionParams = {
  db_type: "postgres",
  host: "",
  port: undefined,
  database: "",
  schema_name: "",
  username: "",
  password: "",
  auth_type: "password",
  account: "",
  warehouse: "",
  role: "",
  table_or_query: "",
};

export default function DatabaseConnect({
  onComplete,
  onBack,
  mode = "profiler",
  onMdmConnectorCreated,
  initialValues,
}: Props) {
  const { projectId } = useParams<{ projectId: string }>();
  const [dbType, setDbType] = useState(initialValues?.db_type ?? "postgres");
  const [form, setForm] = useState<DatabaseConnectionParams>({
    ...EMPTY_FORM,
    ...initialValues,
  });
  useEffect(() => {
    if (initialValues) return;
    fetch("/db-defaults.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((defaults) => {
        if (!defaults) return;
        setDbType(defaults.db_type ?? "postgres");
        setForm((prev) => ({ ...prev, ...defaults }));
      })
      .catch(() => {});
  }, []);

  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const update = (
    key: keyof DatabaseConnectionParams,
    value: string | number,
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleConnect = async () => {
    setError(null);
    setTestResult(null);
    setConnecting(true);
    try {
      if (mode === "mdm" && onMdmConnectorCreated) {
        const { connectDatabase: mdmConnect } =
          await import("../features/mdm/api/mdm-client");
        const card = await mdmConnect({
          ...form,
          db_type: dbType,
          project_id: projectId,
        });
        onMdmConnectorCreated(card);
      } else {
        const result = await api.connectDatabase({ ...form, db_type: dbType });
        onComplete(result);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const handleTestConnection = async () => {
    setTestResult(null);
    setError(null);
    setTesting(true);
    try {
      let result: { success: boolean; message: string };
      if (mode === "mdm") {
        // MDM mode: use the v2 application test endpoint
        const res = await fetch(
          `/api/v2/projects/${projectId}/test-db-connection`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token":
                document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
            },
            credentials: "include",
            body: JSON.stringify({ ...form, db_type: dbType }),
          },
        );
        if (res.ok) {
          result = await res.json();
        } else {
          const body = await res
            .json()
            .catch(() => ({ detail: res.statusText }));
          result = {
            success: false,
            message: body.detail || `Error ${res.status}`,
          };
        }
      } else {
        result = await api.testConnection({ ...form, db_type: dbType });
      }
      setTestResult(result);
    } catch (err: unknown) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : "Test failed",
      });
    } finally {
      setTesting(false);
    }
  };

  const isSnowflake = dbType === "snowflake";
  const isOracle = dbType === "oracle";
  const defaultPort = dbType === "mysql" ? "3306" : isOracle ? "1521" : "5432";

  return (
    <div className="h-full overflow-y-auto custom-scrollbar relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4 py-10">
        <button
          onClick={onBack}
          data-testid="db-connect-back"
          className="self-start flex items-center gap-2 text-sm text-white/35 hover:text-white transition-colors ml-4"
        >
          <ArrowLeft size={16} /> Back to file upload
        </button>

        <h2 className="text-2xl font-bold text-white">Connect to Database</h2>

        {/* DB type selector */}
        <div className="flex flex-wrap justify-center gap-2 w-full max-w-2xl">
          {DB_TYPES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => {
                setDbType(value);
                update("db_type", value);
              }}
              data-testid="db-connect-type"
              data-value={value}
              className={`
              flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all
              ${
                dbType === value
                  ? "text-white"
                  : "text-white/35 hover:text-white/60"
              }
            `}
              style={
                dbType === value
                  ? {
                      background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                      boxShadow: "0 0 15px rgba(134,188,37,0.2)",
                    }
                  : {
                      background: "rgba(28,28,42,0.5)",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }
              }
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="w-full max-w-2xl space-y-4 glass-card rounded-2xl p-6">
          {isSnowflake ? (
            <>
              <Field
                label="Account"
                value={form.account || ""}
                onChange={(v) => update("account", v)}
                placeholder="abc12345.us-east-1"
              />
              <Field
                label="Warehouse"
                value={form.warehouse || ""}
                onChange={(v) => update("warehouse", v)}
                placeholder="COMPUTE_WH"
              />
              <Field
                label="Role"
                value={form.role || ""}
                onChange={(v) => update("role", v)}
                placeholder="SYSADMIN"
              />
            </>
          ) : (
            <>
              <Field
                label="Host"
                value={form.host || ""}
                onChange={(v) => update("host", v)}
                placeholder={
                  isOracle ? "host.docker.internal" : "hostname or IP"
                }
                testId="db-connect-host"
              />
              {/* Warn immediately if the user types localhost — it resolves to
                  the container itself inside Docker, not to the DB service. */}
              {["localhost", "127.0.0.1", "::1"].includes(
                (form.host || "").trim().toLowerCase(),
              ) && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200 -mt-2">
                  <AlertTriangle
                    size={12}
                    className="mt-0.5 shrink-0 text-amber-400"
                  />
                  <span>
                    <strong className="text-amber-300">localhost</strong>{" "}
                    resolves to the container itself inside Docker — it cannot
                    reach your database.{" "}
                    {form.host && (
                      <>
                        For the local dev stack, use{" "}
                        <code className="font-mono text-amber-300 bg-amber-500/10 px-1 rounded">
                          db-v2
                        </code>{" "}
                        as the host (Docker Compose service name). For an
                        external database, use its real hostname or IP address.
                      </>
                    )}
                  </span>
                </div>
              )}
              {isOracle &&
                !["localhost", "127.0.0.1", "::1"].includes(
                  (form.host || "").trim().toLowerCase(),
                ) && (
                  <p className="text-[11px] text-white/30 -mt-2">
                    Using an SSH/AWS tunnel? Use{" "}
                    <span className="font-mono text-[#86bc25]/70">
                      host.docker.internal
                    </span>{" "}
                    instead of{" "}
                    <span className="font-mono text-white/40">localhost</span>
                  </p>
                )}
              <Field
                label="Port"
                value={String(form.port || "")}
                onChange={(v) => update("port", parseInt(v) || 0)}
                placeholder={defaultPort}
                testId="db-connect-port"
              />
            </>
          )}

          <Field
            label={isOracle ? "Service Name" : "Database"}
            value={form.database || ""}
            onChange={(v) => update("database", v)}
            placeholder={isOracle ? "ORCL" : "my_database"}
            testId="db-connect-database"
          />
          <Field
            label="Schema"
            value={form.schema_name || ""}
            onChange={(v) => update("schema_name", v)}
            placeholder="public"
            testId="db-connect-schema"
          />
          <Field
            label="Username"
            value={form.username || ""}
            onChange={(v) => update("username", v)}
            placeholder="user"
            testId="db-connect-username"
          />
          <Field
            label={
              form.auth_type === "pat"
                ? "Personal Access Token (PAT)"
                : "Password"
            }
            value={form.password || ""}
            onChange={(v) => update("password", v)}
            placeholder="••••••••"
            type="password"
            testId="db-connect-password"
          />

          {/* Auth type selector */}
          <div>
            <label className="block text-xs font-medium text-white/40 mb-1.5">
              Authentication Method
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => update("auth_type", "password")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                  form.auth_type === "password"
                    ? "text-white"
                    : "text-white/35 hover:text-white/60"
                }`}
                style={
                  form.auth_type === "password"
                    ? {
                        background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                      }
                    : {
                        background: "rgba(28,28,42,0.5)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }
                }
              >
                <Lock size={14} /> Password
              </button>
              <button
                onClick={() => update("auth_type", "pat")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                  form.auth_type === "pat"
                    ? "text-white"
                    : "text-white/35 hover:text-white/60"
                }`}
                style={
                  form.auth_type === "pat"
                    ? {
                        background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                      }
                    : {
                        background: "rgba(28,28,42,0.5)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }
                }
              >
                <Key size={14} /> Personal Access Token (PAT)
              </button>
            </div>
            {form.auth_type === "pat" && (
              <p className="text-[11px] text-white/25 mt-1">
                PAT is used in place of password for Snowflake and other
                providers that support token-based auth.
              </p>
            )}
          </div>

          <div className="pt-2">
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Table or SQL Query
            </label>
            <textarea
              value={form.table_or_query || ""}
              onChange={(e) => update("table_or_query", e.target.value)}
              placeholder="my_table  or  SELECT * FROM my_table LIMIT 10000"
              rows={3}
              data-testid="db-connect-table-or-query"
              className="w-full px-3 py-2 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none font-mono transition border"
              style={{
                background: "rgba(14,14,24,0.6)",
                borderColor: "rgba(255,255,255,0.08)",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "rgba(134,188,37,0.4)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              }}
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {testResult && (
            <div
              data-testid="db-connect-test-result"
              data-success={testResult.success}
              className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
                testResult.success
                  ? "bg-green-500/10 border border-green-500/30 text-green-400"
                  : "bg-red-500/10 border border-red-500/30 text-red-400"
              }`}
            >
              {testResult.success ? (
                <Check size={16} />
              ) : (
                <AlertTriangle size={16} />
              )}
              {testResult.message}
            </div>
          )}

          {/* Test Connection button */}
          <button
            onClick={handleTestConnection}
            disabled={testing || !form.database || !form.username}
            data-testid="db-connect-test"
            className="w-full py-2.5 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-30 border"
            style={{
              background: "rgba(28,28,42,0.5)",
              borderColor:
                testing || !form.database || !form.username
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(134,188,37,0.4)",
            }}
          >
            {testing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Plug size={16} />
                Test Connection
              </>
            )}
          </button>

          <button
            onClick={handleConnect}
            disabled={connecting || !form.database || !form.table_or_query}
            data-testid="db-connect-submit"
            className="w-full py-3 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-30"
            style={{
              background:
                connecting || !form.database || !form.table_or_query
                  ? "rgba(255,255,255,0.05)"
                  : "linear-gradient(135deg, #86BC25, #6B9B1E)",
              boxShadow:
                connecting || !form.database || !form.table_or_query
                  ? "none"
                  : "0 0 20px rgba(134,188,37,0.2)",
            }}
          >
            {connecting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Connecting & profiling...
              </>
            ) : (
              <>
                <Database size={18} />
                Connect & Profile
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-white/40 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none transition border"
        style={{
          background: "rgba(14,14,24,0.6)",
          borderColor: "rgba(255,255,255,0.08)",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "rgba(134,188,37,0.4)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        }}
      />
    </div>
  );
}
